/* eslint-disable @typescript-eslint/no-explicit-any */
import cx from 'classnames';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'semantic-ui-react';

import { bem } from '../../../core/design/bem';
import { useProgramStoreSync } from '../../../group-visits/hooks/useProgramStore';
import { getSlotDates, toProgramIds } from '../../../group-visits/utils';
import { useMe } from '../../../profile/hooks';
import BlockContainer from '../../BlockContainer';
import WorkshopDate from '../../workshops/WorkshopDate';
import { BlockProps, ContainerProps } from '../types';
import './GroupRecapBlock.scss';
import { recapItemComponents } from './recapItemComponents';

const css = bem('GroupRecapBlock');

/** La donnée peut arriver déjà parsée, ou sous forme de chaîne JSON (imports de données) */
function parseGroups(value: any): any {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    console.warn('Invalid group-recap json data', value);
    return undefined;
  }
}

type RecapItemConfig = {
  /** Champ du groupe contenant les ids à afficher */
  field: string;
  /** Petit titre affiché au-dessus de la liste */
  label?: string;
  /** Type d'affichage */
  type: 'users' | 'workshopSessions';
  /** Collection de la donnée */
  collection: string;
  [x: string]: any;
};

type GroupRecapBlockProps = BlockProps & {
  blocks?: RecapItemConfig[];
  className?: string;
  /** Id html du bloc (ancre) */
  id?: string;
  container?: ContainerProps;
  dateConfig?: Record<string, any>;
  /** Champ de la data participant contenant les groupes */
  field?: string;
  /** Champ du groupe contenant l'invitant */
  inviterField?: string;
  inviterLabel?: string;
  /** Champ du groupe contenant le créneau "YYYY-MM-DD HH:mm - HH:mm" */
  slotField?: string;
  /** Durée du créneau (minutes) quand seule l'heure de début est fournie */
  slotDuration?: number;
};

type GroupRecapItemProps = {
  block: RecapItemConfig;
  group: Record<string, any>;
};

const GroupRecapItem: FC<GroupRecapItemProps> = ({ block, group }) => {
  const { field, label, type, collection, ...itemConfig } = block;
  const ids = useMemo(() => toProgramIds(get(group, field)), [group, field]);
  const ItemComponent = recapItemComponents[type];

  if (!ItemComponent) {
    console.warn('Unknown group-recap block type', type);
    return null;
  }
  if (!ids.length) return null;

  return (
    <div className={css('item', { type, field }).toString()}>
      <ItemComponent {...itemConfig} ids={ids} label={label} collection={collection} />
    </div>
  );
};

type GroupRecapGroupProps = Omit<GroupRecapItemProps, 'block'> & {
  blocks: RecapItemConfig[];
  dateConfig?: Record<string, any>;
  inviterField: string;
  inviterLabel?: string;
  slotDuration?: number;
  slotField: string;
};

const GroupRecapGroup: FC<GroupRecapGroupProps> = (props) => {
  const { blocks, dateConfig, group, inviterField, inviterLabel, slotDuration, slotField } = props;
  const { t } = useTranslation();
  const { startDate, endDate } = getSlotDates(group, { slotField, slotDuration });
  const inviter = get(group, inviterField) || group.inviterName;

  return (
    <div className={css('group').toString()}>
      <div className={css('group-header').toString()}>
        {startDate && (
          <WorkshopDate
            className={css('date').toString()}
            startDate={startDate}
            endDate={endDate}
            showDay
            showEndTime
            dateConfig={dateConfig}
          />
        )}
        {inviter && (
          <div className={css('inviter').toString()}>
            <Icon name="user outline" />
            <span className={css('inviter-label').toString()}>
              {inviterLabel || t('blocks.group-recap.inviter')}
            </span>{' '}
            <strong>{inviter}</strong>
          </div>
        )}
      </div>
      {blocks.map((block) => (
        <GroupRecapItem key={`${block.type}-${block.field}`} block={block} group={group} />
      ))}
    </div>
  );
};

const GroupRecapBlock: FC<GroupRecapBlockProps> = (props) => {
  const {
    _id,
    blocks = [],
    className,
    container,
    dateConfig,
    field = 'groups',
    id,
    inviterField = 'inviter',
    inviterLabel,
    slotDuration,
    slotField = 'slot',
  } = props;

  const me = useMe();
  const userCollections = useMemo(
    () => blocks.filter(({ type }) => type === 'users').map(({ collection }) => collection),
    [blocks],
  );
  // Les blocs délégués lisent le store : on s'assure juste qu'il est alimenté et à jour
  useProgramStoreSync(userCollections);

  const groups = useMemo(() => {
    const value = parseGroups(get(me, field));
    if (!value) return [];
    const list = (Array.isArray(value) ? value : [value]).filter((v) => v);
    return orderBy(
      list,
      (group) => getSlotDates(group, { slotField, slotDuration }).startDate || '',
    );
  }, [me, field, slotField, slotDuration]);

  if (!groups.length) return null;

  return (
    <BlockContainer
      id={id}
      {...container}
      className={css({ id: _id }).mix(cx(className, container?.className)).toString()}
    >
      {groups.map((group, index) => (
        <GroupRecapGroup
          // eslint-disable-next-line react/no-array-index-key
          key={group._id || `${group[slotField]}-${index}`}
          blocks={blocks}
          dateConfig={dateConfig}
          group={group}
          inviterField={inviterField}
          inviterLabel={inviterLabel}
          slotDuration={slotDuration}
          slotField={slotField}
        />
      ))}
    </BlockContainer>
  );
};

export default GroupRecapBlock;
